import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

const Terminal: React.FC = () => {
  const [currentCommand, setCurrentCommand] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [userInput, setUserInput] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [isInteractive, setIsInteractive] = useState(false);
  const [systemLoad, setSystemLoad] = useState(85);
  const [isTyping, setIsTyping] = useState(false);  const [currentTypingText, setCurrentTypingText] = useState('');
  const [typingQueue, setTypingQueue] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const commandHistoryRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize static command data to prevent unnecessary re-renders
  const interactiveCommands: { [key: string]: string[] } = useMemo(() => ({'help': [
      'Available commands:',
      '  about         - Learn about PaidNerd',
      '  services      - View our web development services',
      '  portfolio     - View our work portfolio',
      '  contact       - Get in touch',
      '  clear         - Clear terminal',
      '  matrix        - Toggle matrix effect',
      '  hack          - Enter hacker mode 😎'
    ],'about': [
      '🚀 PaidNerd - Elite Web Development Agency',
      '',
      'Founded with a vision to revolutionize web development,',
      'we specialize in creating high-performance web applications',
      'that combine cutting-edge technology with stunning design.',
      '',
      '🔧 Our Tech Stack:',
      '• React/Next.js & Angular (Latest versions)',
      '• Node.js & Python with performance optimization',
      '• PostgreSQL/MongoDB with query optimization',
      '• Docker & containerized deployments',
      '• CI/CD pipelines for seamless updates'
    ],    'services': [
      '📋 Web Development Services:',
      '',
      '🎨 Custom UI/UX Design',
      '   └── Modern interfaces tailored to your brand',
      '',
      '🛒 E-commerce Platforms',
      '   └── Scalable online stores with advanced features',
      '',
      '⚡ Performance Optimization',
      '   └── Lightning-fast loading speeds & SEO',
      '',      '🔒 Security Implementation',
      '   └── Enterprise-grade protection & monitoring',
      '',
      '📱 Responsive Development',
      '   └── Optimized across all devices & platforms',
      '',
      '🔧 Maintenance & Support',
      '   └── 24/7 monitoring and technical updates'
    ],
    'portfolio': [
      '🏆 Recent Projects:',
      '',
      '• E-commerce Platform - Fashion Retailer',
      '  └── 300% increase in conversion rates',
      '',
      '• Corporate Platform - Tech Startup',
      '  └── 95% PageSpeed Insights score',
      '',
      '• Restaurant Chain - Multi-location',
      '  └── Online ordering system integration',
      '',
      '• Non-profit Organization',
      '  └── Donation platform with payment gateway',
      '',
      '📊 Statistics:',
      '• 150+ Successful projects',
      '• 99.9% Client satisfaction rate',
      '• 2.5s Average page load time'
    ],    'contact': [
      '📞 Get In Touch:',
      '',
      '📧 Email: code@paidnerd.com',
      '📱 WhatsApp: +96891212037',
      '🏢 Office: Muscat, Sultanate of Oman',
      '',
      '⏰ Business Hours:',
      'Sunday - Thursday: 9:00 AM - 6:00 PM (GST)',
      '',
      '💬 Ready to start your project?'
    ],
    'matrix': [
      'Matrix effect toggled! 🕶️',
      'Welcome to the real world, Neo...',
      '',
      'The matrix rain is now flowing...',
      'Reality is merely an illusion.',
      '',
      'Red pill or blue pill? 💊'
    ],    'hack': [
      '🕶️ Initiating ULTIMATE hacker mode... 😎',
      '',
      '🔓 Breaking into NASA mainframe...',
      '🌍 Hacking the entire internet...',
      '🦄 Downloading more RAM...',
      '🍕 Ordering pizza with backdoor access...',
      '',
      '█████████████████ 100%',
      '',
      '💀 SYSTEM BREACH DETECTED! 💀',
      '🚨 FBI is typing... 🚨',
      '',
      '😂 Just kidding! We only hack together',
      'mind-blowing platforms and hilarious bugs! 🐛✨',
      '',
      '🎭 No servers, pizzas, or unicorns were',
      'harmed in the making of this joke! 🦄🍕'
    ],'clear': []
  }), []);

  // Memoize commands array for better performance
  const commands = useMemo(() => [
    {
      command: 'cat /etc/PaidNerd/about.txt',
      output: [
        '🚀 PaidNerd - Your Website Development Terminal',
        '',
        'We specialize in crafting exceptional web applications',
        'that combine modern design with powerful functionality.',
        '',
        '🎯 Why Choose Our Terminal-Styled Approach?',
        '• Clean, minimalist design philosophy',
        '• Lightning-fast performance optimization',
        '• Developer-friendly code architecture',
        '• Modern security implementations',
        '• SEO-optimized from the ground up',
        '',
        'Type "help" for interactive commands...'
      ]
    },
    {
      command: 'ps aux | grep web-services',
      output: [
        'USER       PID  %CPU %MEM COMMAND',
        'www-data  1234  15.2  8.1 /usr/bin/node web-application',
        'www-data  1235  12.8  6.4 /usr/bin/nginx frontend-server',
        'postgres  1236   8.5  12.3 /usr/bin/postgres database-engine',
        'www-data  1237   5.2  4.1 /usr/bin/redis-server cache-engine',
        'root      1238   2.1  1.8 /usr/bin/fail2ban security-monitor',
        'www-data  1239   1.5  2.2 /usr/bin/pm2 process-manager',
        '',
        '✅ All web development services running optimally!'
      ]
    },
    {
      command: 'netstat -tuln | grep :80',
      output: [
        'Active Internet connections (servers)',
        'Proto Recv-Q Send-Q Local Address   State    Service',
        'tcp   0      0      0.0.0.0:80      LISTEN   nginx/web-server',
        'tcp   0      0      0.0.0.0:443     LISTEN   nginx/ssl',
        'tcp   0      0      127.0.0.1:3306  LISTEN   mysql/database',
        'tcp   0      0      127.0.0.1:6379  LISTEN   redis/cache',
        '',
        '🌐 Web application infrastructure ready for deployment!'
      ]
    }
  ], []);

  // Optimized cursor animation effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, []);

  // Optimized system load animation
  useEffect(() => {
    const loadInterval = setInterval(() => {
      setSystemLoad(prev => Math.max(20, prev + (Math.random() - 0.5) * 10));
    }, 2000);

    return () => clearInterval(loadInterval);
  }, []);

  // Optimized command cycling
  useEffect(() => {
    const commandInterval = setInterval(() => {
      setCurrentCommand(prev => (prev + 1) % commands.length);
    }, 8000);

    return () => clearInterval(commandInterval);
  }, [commands.length]);
  // Character-by-character typing animation
  useEffect(() => {
    if (typingQueue.length > 0 && !isTyping) {
      setIsTyping(true);
      const nextLine = typingQueue[0];
      setTypingQueue(prev => prev.slice(1));
      
      // Add a small delay before starting to type the line
      setTimeout(() => {
        let charIndex = 0;
        setCurrentTypingText('');
        
        const typeNextChar = () => {
          if (charIndex < nextLine.length) {
            setCurrentTypingText(prev => prev + nextLine[charIndex]);
            charIndex++;
            
            // Variable typing speed for more realistic effect
            let delay = 50; // Default delay
            const char = nextLine[charIndex - 1];
            
            if (char === ' ') {
              delay = 80; // Longer pause for spaces
            } else if (['.', '!', '?', ':'].includes(char)) {
              delay = 200; // Longer pause for punctuation
            } else if (char === '\n') {
              delay = 300; // Pause for line breaks
            } else {
              delay = Math.random() * 60 + 20; // Random typing speed
            }
            
            typingTimeoutRef.current = setTimeout(typeNextChar, delay);
          } else {
            // Line complete, add to history and start next line
            setTimeout(() => {
              setCommandHistory(prev => [...prev, nextLine]);
              setCurrentTypingText('');
              setIsTyping(false);
            }, 100); // Small pause before showing next line
          }
        };
        
        typeNextChar();
      }, 150); // Initial delay before starting to type
    }
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [typingQueue, isTyping]);
  // Scroll to bottom when command history updates or when typing
  useEffect(() => {
    if (commandHistoryRef.current) {
      commandHistoryRef.current.scrollTop = commandHistoryRef.current.scrollHeight;
    }
  }, [commandHistory, currentTypingText]);

  // Cleanup typing animation on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);  // Optimized command handler with useCallback
  const handleUserInput = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isTyping) {
      const command = userInput.trim().toLowerCase();
      setCommandHistory(prev => [...prev, `PaidNerd@webdev:~$ ${userInput}`]);
      
      if (interactiveCommands[command]) {
        if (command === 'clear') {
          setCommandHistory([]);
          setTypingQueue([]);
          setCurrentTypingText('');
        } else {
          // Add command output to typing queue for animation
          setTypingQueue(prev => [...prev, ...interactiveCommands[command], '']);
        }
      } else if (command) {
        // Add error message to typing queue
        setTypingQueue(prev => [...prev, `Command not found: ${command}`, 'Type "help" for available commands.', '']);
      }
      
      setUserInput('');
    } else if (isTyping && e.key !== 'Tab') {
      // Skip typing animation when user presses any key (except Tab)
      e.preventDefault();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Complete current line immediately
      const currentLine = typingQueue.length > 0 ? typingQueue[0] : '';
      if (currentLine || currentTypingText) {
        const lineToAdd = currentLine || currentTypingText;
        setCommandHistory(prev => [...prev, lineToAdd]);
        setCurrentTypingText('');
        
        // Add remaining lines without animation
        if (typingQueue.length > 1) {
          const remainingLines = typingQueue.slice(1);
          setCommandHistory(prev => [...prev, ...remainingLines]);
        }
        
        setTypingQueue([]);
        setIsTyping(false);
      }
    }  }, [userInput, interactiveCommands, isTyping, typingQueue, currentTypingText]);

  return (
    <section className="section terminal-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title glitch-text" data-text="Interactive Terminal">
            Interactive Terminal
          </h2>          <div className="system-monitor">
            <div className="monitor-item">
              <span className="monitor-label">CPU:</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: `${systemLoad}%`}}></div>
              </div>
              <span className="monitor-value">{systemLoad.toFixed(1)}%</span>
            </div>
            <div className="monitor-item">
              <span className="monitor-label">Memory:</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '64%'}}></div>
              </div>
              <span className="monitor-value">6.4GB/10GB</span>
            </div>
            <div className="monitor-item">
              <span className="monitor-label">Network:</span>
              <span className="status-indicator online">●</span>
              <span className="monitor-value">Online</span>
            </div>
          </div>
        </div>

        <div className="terminal-grid">
          <div className="terminal-window demo-terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <div className="terminal-dot red pulse"></div>
                <div className="terminal-dot yellow pulse"></div>
                <div className="terminal-dot green pulse"></div>
              </div>              <span className="terminal-window-title">Demo - Web Development Environment</span>
            </div>
            <div className="terminal-content">              <div className="command-section">
                <div className="terminal-line">
                  <span className="terminal-prompt">PaidNerd@webdev:~$</span>{' '}
                  <span className="terminal-command">{commands[currentCommand].command}</span>
                </div>
                <div className="terminal-output">
                  {commands[currentCommand].output.map((line, index) => (
                    <div key={index} className="output-line">
                      {line.includes('✅') ? (
                        <span className="highlight-green">{line}</span>
                      ) : line.includes('🚀') || line.includes('🎯') ? (
                        <span className="highlight-cyan">{line}</span>
                      ) : line.includes('•') ? (
                        <span className="highlight-yellow">{line}</span>
                      ) : (
                        line
                      )}
                    </div>
                  ))}
                </div>              </div>
            </div>
          </div><div className="terminal-window interactive-terminal">            <div className="terminal-header">
              <div className="terminal-dots">
                <div className="terminal-dot red pulse"></div>
                <div className="terminal-dot yellow pulse"></div>
                <div className="terminal-dot green pulse"></div>
              </div>              <span className="terminal-window-title">Interactive Shell - Try Commands!</span>
              <button 
                className="btn-status"
                onClick={() => setIsInteractive(!isInteractive)}
                title={isInteractive ? 'View Mode' : 'Interactive Mode'}
              >
                <span className="status-dot"></span>
                {isInteractive ? 'View' : 'Shell'}              </button>
            </div>
            <div className="terminal-content">
              {!isInteractive ? (
                <div className="interactive-prompt">
                  <div className="prompt-message">
                    <span className="highlight-cyan">Click the status button above to start interactive mode!</span>
                  </div>                  <div className="prompt-help">
                    <div>Available commands: help, about, services, portfolio, contact</div>
                    <div>Special commands: matrix, hack, clear</div>
                  </div>
                </div>              ) : (
                <>                  <div className="command-history" ref={commandHistoryRef}>
                    {commandHistory.map((line, index) => (
                      <div key={index} className="history-line">
                        {line.startsWith('PaidNerd@webdev:~$') ? (
                          <span className="user-command">{line}</span>
                        ) : (
                          <span className="command-output">{line}</span>
                        )}
                      </div>
                    ))}
                    {/* Show currently typing text */}
                    {isTyping && currentTypingText && (
                      <div className="history-line typing-line">
                        <span className="command-output">
                          {currentTypingText}
                          <span className="typing-cursor">█</span>
                        </span>
                      </div>
                    )}                  </div>
                    <div className="input-section">
                    <span className="terminal-prompt">PaidNerd@webdev:~$</span>{' '}
                    <input
                      ref={inputRef}
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={handleUserInput}
                      className="terminal-input"
                      placeholder={isTyping ? "Processing..." : "Type 'help' for commands..."}
                      disabled={isTyping}
                      autoFocus
                    />
                    <span className={`cursor ${showCursor ? 'visible' : ''}`}>█</span>
                  </div>
                </>
              )}
            </div>
          </div>        </div>

        <div className="tech-showcase" id="tech-showcase">
          <h3 className="showcase-title">Technology Stack</h3><div className="tech-grid">
            <div className="tech-item">
              <div className="tech-icon">🚀</div>
              <div className="tech-name">JavaScript/Node.js</div>
              <div className="tech-desc">Scalable backend & real-time APIs</div>
            </div>
            <div className="tech-item">
              <div className="tech-icon">⚛️</div>
              <div className="tech-name">React/Angular/Vue.js</div>
              <div className="tech-desc">Modern, component-based frontends</div>
            </div>
            <div className="tech-item">
              <div className="tech-icon">🐍</div>
              <div className="tech-name">Python/Django/Flask</div>
              <div className="tech-desc">Robust, secure web frameworks</div>
            </div>            <div className="tech-item">
              <div className="tech-icon">🐘</div>
              <div className="tech-name">PHP/Laravel</div>
              <div className="tech-desc">Dynamic, enterprise-grade applications</div>
            </div>
            <div className="tech-item">
              <div className="tech-icon">📝</div>
              <div className="tech-name">WordPress</div>
              <div className="tech-desc">Content management & custom themes</div>
            </div>
            <div className="tech-item">
              <div className="tech-icon">📚</div>
              <div className="tech-name">TypeScript/JavaScript</div>
              <div className="tech-desc">Modern language features & type safety</div>
            </div>
            <div className="tech-item">
              <div className="tech-icon">🗄️</div>
              <div className="tech-name">PostgreSQL/MySQL/MongoDB</div>
              <div className="tech-desc">Relational & NoSQL database systems</div>
            </div>
            <div className="tech-item">
              <div className="tech-icon">🔒</div>
              <div className="tech-name">OAuth2/SSL/TLS</div>
              <div className="tech-desc">Enterprise-grade security protocols</div>
            </div>
          </div></div>
      </div>
    </section>
  );
};

export default Terminal;
